import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import InputAdornment from '@mui/material/InputAdornment/index';
import IconButton from '@mui/material/IconButton/index';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  fetchUser,
  updateUserSecuritySettings,
  fetchAccount
} from '#action/user/settings';
import { reduxFormResolver } from '#helper/rhf-resolver';
import RHFCheckbox from '#component/form/rhf/rhf-checkbox';
import RHFTextField from '#component/form/rhf/rhf-text-field';
import {
  ButtonsContainer,
  SecurityCnt,
  StyledBtn,
  StyledDivider,
  StyledPaper,
  StyledSubTitle,
  StyledTitle
} from './styles';
import validate from './validate';

const passwordHelperText = 'For security, your password must be at least 8 characters long. It must contain letters, numbers and at least one special character.';

const UserSecurityForm = props => {
  const { history } = props;
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const account = useSelector(s => s.account, shallowEqual);
  const user = useSelector(s => s.user, shallowEqual);
  const dispatch = useDispatch();

  const methods = useForm({
    resolver: reduxFormResolver(validate),
    defaultValues: {
      password: '',
      confirmPassword: '',
      twoFactorAuth: !!user?.twoFactorAuth
    }
  });

  const { handleSubmit, reset, formState: { isDirty, isSubmitting } } = methods;

  useEffect(() => {
    reset({
      password: '',
      confirmPassword: '',
      twoFactorAuth: !!user?.twoFactorAuth
    });
  }, [user, reset]);

  const handleFormSubmit = async values => {
    await dispatch(updateUserSecuritySettings(user.id, {
      password: values.password || undefined,
      twoFactorAuth: Number(values.twoFactorAuth)
    }));

    await dispatch(fetchUser(user.id));

    if (account.id === user.id) {
      await dispatch(fetchAccount({ ignoreLoading: true }));
    }

    reset({
      password: '',
      confirmPassword: '',
      twoFactorAuth: values.twoFactorAuth
    }, { keepDirty: false });
  };

  return (
    <SecurityCnt>
      <FormProvider {...methods}>
        <form autoComplete="off" onSubmit={handleSubmit(handleFormSubmit)}>
          <StyledTitle variant="h4" gutterBottom>
            Security
          </StyledTitle>
          <StyledPaper elevation={0}>
            <StyledSubTitle variant="subtitle1">
              Authentication
            </StyledSubTitle>
            <RHFCheckbox
              name="twoFactorAuth"
              label="Enable two-factor authentication"
            />
            <StyledDivider />
            <StyledSubTitle variant="subtitle1" gutterBottom>
              Change password
            </StyledSubTitle>
            <RHFTextField
              name="password"
              label="Password"
              variant="outlined"
              fullWidth
              margin="normal"
              info={passwordHelperText}
              type={showPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Toggle password visibility"
                      onClick={() =>
                        setShowPassword(state => !state)}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <RHFTextField
              name="confirmPassword"
              label="Confirm password"
              variant="outlined"
              fullWidth
              margin="normal"
              type={showConfirmPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Toggle password visibility"
                      onClick={() =>
                        setShowConfirmPassword(state => !state)}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </StyledPaper>
          <ButtonsContainer>
            <StyledBtn
              variant="outlined"
              onClick={() => {
                history.goBack();
              }}
            >
              Cancel
            </StyledBtn>
            <StyledBtn
              disabled={!isDirty || isSubmitting}
              type="submit"
              variant="contained"
              color="secondary"
            >
              Save
            </StyledBtn>
          </ButtonsContainer>
        </form>
      </FormProvider>
    </SecurityCnt>
  );
};

export default UserSecurityForm;
